import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, setPersistence, browserLocalPersistence, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, setDoc, collection, addDoc, getDoc, query, where, getDocs, orderBy, updateDoc, writeBatch, serverTimestamp, deleteDoc, limit, Timestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const firebaseConfig = {
  apiKey: "AIzaSyBJHWQdO5CksTb0hduVVOcQg6yxmxSGGX0",
  authDomain: "speil-tdy.firebaseapp.com",
  projectId: "speil-tdy",
  storageBucket: "speil-tdy.firebasestorage.app",
  messagingSenderId: "869697921219",
  appId: "1:869697921219:web:9a9b5e9f45e73ac80d0276",
  measurementId: "G-G51P099ZX8"
};

let app;
let auth;
let db;
let storage;
let analytics;

try {
  // Initialize Firebase
  app = initializeApp(firebaseConfig);
  analytics = getAnalytics(app);
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);

  // Set persistence
  setPersistence(auth, browserLocalPersistence)
    .catch(error => {
      console.error("Persistence error:", error);
    });

  // Auth state listener
  onAuthStateChanged(auth, (user) => {
    if (user) {
      console.log('User is signed in:', user.uid);
    } else {
      console.log('User is signed out');
    }
  });
} catch (error) {
  console.error("Firebase initialization error:", error);
}

// Authentication functions
export const signUp = async (email, password, name, handle, episodeData) => {
  try {
    // Create user account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    
    // Upload episode image
    const storage = getStorage();
    const imageRef = ref(storage, `users/${userId}/shows/main/episodes/pilot`);
    await uploadBytes(imageRef, episodeData.image);
    const imageUrl = await getDownloadURL(imageRef);
    
    // Upload supplemental images if any
    const supplementalImageUrls = [];
    if (episodeData.supplementalImages && episodeData.supplementalImages.length > 0) {
      for (let i = 0; i < episodeData.supplementalImages.length; i++) {
        const sceneRef = ref(storage, `users/${userId}/shows/main/episodes/pilot/scenes/${i}`);
        await uploadBytes(sceneRef, episodeData.supplementalImages[i]);
        const sceneUrl = await getDownloadURL(sceneRef);
        supplementalImageUrls.push(sceneUrl);
      }
    }
    
    const now = Timestamp.now();
    
    // Create user document
    await setDoc(doc(db, "users", userId), {
      id: userId,
      handle: handle,
      email: email,
      name: name,
      createdAt: now,
      hasCreatedPilot: true
    });
    
    // Create show document
    const showId = `${userId}_main`;
    await setDoc(doc(db, "shows", showId), {
      id: showId,
      showId: showId,
      showName: episodeData.showName || `${name}'s Show`,
      showImage: imageUrl, // Use the pilot episode image as show cover
      showDescription: episodeData.showDescription || `${name}'s personal show on Speil`,
      showCreationDate: now,
      showCreator: userId,
      showCollaborators: {
        [userId]: {
          id: userId,
          name: name,
          handle: handle,
          role: 'owner'
        }
      },
      showCastSeason: {
        "1": [name]
      },
      currentSeason: 1,
      showPossibleScore: 0,
      showEarnedScore: 0,
      seasonScores: {
        "1": {
          seasonEarnedScore: 0,
          seasonPossibleScore: 0
        }
      },
      showStreak: 1,
      showStreakExpirationDate: Timestamp.fromDate(new Date(Date.now() + 86400000)), // 24 hours from now
      streakExpirationDate: Timestamp.fromDate(new Date(Date.now() + 86400000))
    });
    
    // Create reference to show in user's shows subcollection
    await setDoc(doc(db, "users", userId, "shows", showId), {
      id: showId,
      showName: episodeData.showName || `${name}'s Show`,
      addedAt: now
    });
    
    // Create episode document
    const episodeId = `${showId}_pilot`;
    await setDoc(doc(db, "shows", showId, "episodes", episodeId), {
      id: episodeId,
      episodeTitle: episodeData.title || "Pilot",
      episodeDescription: episodeData.description,
      episodeImage: imageUrl,
      episodeDuration: parseInt(episodeData.duration),
      episodeCreationDate: now,
      episodeDate: Timestamp.fromDate(episodeData.date),
      episodeShowId: showId,
      episodeSeason: 1,
      episodeCreator: userId,
      episodeScenes: supplementalImageUrls,
      episodeRatings: {},
      collaborators: {
        [userId]: {
          id: userId,
          name: name,
          handle: handle,
          role: 'host'
        }
      },
      episodeTotalScore: 0,
      episodeMaximumPossibleScore: 0
    });
    
    return userCredential.user;
  } catch (error) {
    console.error("Error in signUp:", error);
    throw error;
  }
};

// Add this function to fetch user's episode for today
export const getTodaysEpisode = async (userId) => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const episodesRef = collection(db, "users", userId, "episodes");
    const q = query(episodesRef, where("date", ">=", today));
    const episodeSnapshot = await getDocs(q);
    
    if (!episodeSnapshot.empty) {
      return episodeSnapshot.docs[0].data();
    }
    return null;
  } catch (error) {
    throw error;
  }
};

// Update the isDateAvailableForEpisode function
export const isDateAvailableForEpisode = async (userId, date) => {
  try {
    const episodesRef = collection(db, "users", userId, "episodes");
    
    // Create start and end of the selected date
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    
    // Only check for episodes on the same date
    const q = query(episodesRef, 
      where("date", ">=", startOfDay),
      where("date", "<=", endOfDay)
    );
    
    const episodeSnapshot = await getDocs(q);
    return episodeSnapshot.empty;
  } catch (error) {
    throw error;
  }
};

// Add function to get episode count for season
export const getSeasonEpisodeCount = async (userId, season) => {
  try {
    const episodesRef = collection(db, "users", userId, "episodes");
    const q = query(episodesRef, where("season", "==", season));
    const episodeSnapshot = await getDocs(q);
    return episodeSnapshot.size;
  } catch (error) {
    throw error;
  }
};

// Update the uploadDailyEpisode function
export const uploadDailyEpisode = async (userId, episodeData) => {
  try {
    const userEpisodesRef = collection(db, `users/${userId}/episodes`);
    
    // Create the episode document
    await addDoc(userEpisodesRef, {
      userId,
      imageUrl: episodeData.imageUrl,
      description: episodeData.description,
      duration: episodeData.duration,
      season: episodeData.season,
      title: episodeData.title,
      date: episodeData.date,
      supplementalImages: episodeData.supplementalImages || [], // Add supplemental images array
      createdAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error uploading episode:', error);
    throw error;
  }
};

// Friend management
export const addFriend = async (userId, friendHandle) => {
  try {
    // Find user by handle
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("handle", "==", friendHandle));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error("User not found");
    }

    const friendDoc = querySnapshot.docs[0];
    const friendId = friendDoc.id;
    
    // Check if friend count is less than 10
    const userDoc = await getDoc(doc(db, "users", userId));
    const friendDoc2 = await getDoc(doc(db, "users", friendId));
    
    if (userDoc.data().friendCount >= 10 || friendDoc2.data().friendCount >= 10) {
      throw new Error("Friend limit reached");
    }

    // Add friend to both users
    await setDoc(doc(db, "users", userId, "friends", friendId), {
      addedAt: new Date()
    });
    await setDoc(doc(db, "users", friendId, "friends", userId), {
      addedAt: new Date()
    });

    // Update friend counts
    await setDoc(doc(db, "users", userId), {
      ...userDoc.data(),
      friendCount: userDoc.data().friendCount + 1
    });
    await setDoc(doc(db, "users", friendId), {
      ...friendDoc2.data(),
      friendCount: friendDoc2.data().friendCount + 1
    });
  } catch (error) {
    throw error;
  }
};

// Modify getFriendsEpisodes to include navigation data
export const getFriendsEpisodes = async (userId) => {
  try {
    const friendsRef = collection(db, "users", userId, "friends");
    const friendsSnapshot = await getDocs(friendsRef);
    
    const episodes = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    for (const friend of friendsSnapshot.docs) {
      const friendId = friend.id;
      const friendData = await getDoc(doc(db, "users", friendId));
      if (!friendData.exists()) continue;
      
      const friendProfile = friendData.data();
      const episodesRef = collection(db, "users", friendId, "episodes");
      const q = query(
        episodesRef, 
        where("date", ">=", today),
        where("date", "<", tomorrow)
      );
      const episodeSnapshot = await getDocs(q);
      
      // Include all necessary friend data for navigation
      episodes.push({
        userId: friendId,
        userName: friendProfile.name,
        userHandle: friendProfile.handle,
        showName: friendProfile.showName || '',
        showPicture: friendProfile.showPicture || '',
        showBio: friendProfile.showBio || '',
        currentSeason: friendProfile.currentSeason || 1,
        episode: !episodeSnapshot.empty ? {
          ...episodeSnapshot.docs[0].data(),
          id: episodeSnapshot.docs[0].id
        } : null
      });
    }
    
    return episodes.sort((a, b) => {
      if (a.episode && !b.episode) return -1;
      if (!a.episode && b.episode) return 1;
      return a.userName.localeCompare(b.userName);
    });
  } catch (error) {
    console.error("Error fetching friends' episodes:", error);
    throw error;
  }
};

// Update sendFriendRequest to include names and handles
export const sendFriendRequest = async (senderId, receiverHandle) => {
  try {
    // First find the receiver by handle
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('handle', '==', receiverHandle));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('User not found');
    }

    const receiver = querySnapshot.docs[0];
    const receiverData = receiver.data();
    const sender = await getDoc(doc(db, 'users', senderId));
    const senderData = sender.data();

    // Check if request already exists
    const existingRequestQuery = query(
      collection(db, 'notifications'),
      where('type', '==', 'friendRequest'),
      where('senderId', '==', senderId),
      where('receiverId', '==', receiver.id),
      where('status', '==', 'pending')
    );
    
    const existingRequest = await getDocs(existingRequestQuery);
    if (!existingRequest.empty) {
      throw new Error('Friend request already sent');
    }

    // Check if they're already friends
    const existingFriend = await getDoc(doc(db, 'users', senderId, 'friends', receiver.id));
    if (existingFriend.exists()) {
      throw new Error('Already friends with this user');
    }

    // Create notification document with full names and handles
    await addDoc(collection(db, 'notifications'), {
      type: 'friendRequest',
      senderId,
      senderName: senderData.name,
      senderHandle: senderData.handle,
      receiverId: receiver.id,
      receiverName: receiverData.name,
      receiverHandle: receiverData.handle,
      status: 'pending',
      timestamp: serverTimestamp()
    });

  } catch (error) {
    console.error('Error sending friend request:', error);
    throw error;
  }
};

// Update getReceivedFriendRequests to get both types of requests
export const getReceivedFriendRequests = async (userId) => {
  try {
    const requestsQuery = query(
      collection(db, 'notifications'),
      where('receiverId', '==', userId),
      where('status', '==', 'pending'),
      // Get both friend requests and collab requests
      where('type', 'in', ['friendRequest', 'collab'])
    );
    
    const requestsSnapshot = await getDocs(requestsQuery);
    return requestsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting received requests:', error);
    throw error;
  }
};

// Update getSentFriendRequests to get both types of requests
export const getSentFriendRequests = async (userId) => {
  try {
    const requestsQuery = query(
      collection(db, 'notifications'),
      where('senderId', '==', userId),
      where('status', '==', 'pending'),
      // Get both friend requests and collab requests
      where('type', 'in', ['friendRequest', 'collab'])
    );
    
    const requestsSnapshot = await getDocs(requestsQuery);
    return requestsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting sent requests:', error);
    throw error;
  }
};

// Update handleAcceptRequest to include names and handles in friends collection
export const handleAcceptRequest = async (userId, requestId) => {
  try {
    const requestRef = doc(db, 'notifications', requestId);
    const request = await getDoc(requestRef);
    
    if (!request.exists()) {
      throw new Error('Request not found');
    }

    const requestData = request.data();
    const batch = writeBatch(db);

    // Add to sender's friends collection with receiver's name and handle
    batch.set(doc(db, 'users', requestData.senderId, 'friends', userId), {
      id: userId,
      name: requestData.receiverName,
      handle: requestData.receiverHandle,
      timestamp: serverTimestamp()
    });

    // Add to receiver's friends collection with sender's name and handle
    batch.set(doc(db, 'users', userId, 'friends', requestData.senderId), {
      id: requestData.senderId,
      name: requestData.senderName,
      handle: requestData.senderHandle,
      timestamp: serverTimestamp()
    });

    // Update notification status
    batch.update(requestRef, {
      status: 'accepted',
      acceptedAt: serverTimestamp()
    });

    await batch.commit();
  } catch (error) {
    console.error('Error accepting friend request:', error);
    throw error;
  }
};

// Update handleDeclineRequest to use notifications
export const handleDeclineRequest = async (userId, requestId) => {
  try {
    const requestRef = doc(db, 'notifications', requestId);
    await updateDoc(requestRef, {
      status: 'declined',
      declinedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error declining friend request:', error);
    throw error;
  }
};

// Update handleCancelRequest to use notifications
export const handleCancelRequest = async (receiverId, requestId) => {
  try {
    const requestRef = doc(db, 'notifications', requestId);
    await updateDoc(requestRef, {
      status: 'cancelled',
      cancelledAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error canceling friend request:', error);
    throw error;
  }
};

// Update getFriendsList to include friend details
export const getFriendsList = async (userId) => {
  try {
    const friendsRef = collection(db, 'users', userId, 'friends');
    const friendsSnapshot = await getDocs(friendsRef);
    
    return friendsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting friends list:', error);
    throw error;
  }
};

// Add login function
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

// Add sign out function
export const signOut = async () => {
  try {
    await auth.signOut();
  } catch (error) {
    throw error;
  }
};

// Add function to update cast list
export const updateCastList = async (userId, season, castList) => {
  try {
    const seasonRef = doc(db, "users", userId, "seasons", season.toString());
    await setDoc(seasonRef, {
      castList,
      createdAt: new Date()
    });
  } catch (error) {
    throw error;
  }
};

// Modify startNewSeason to include cast list
export const startNewSeason = async (userId, castList) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    const currentSeason = userDoc.data().currentSeason || 1;
    const newSeason = currentSeason + 1;
    
    // Update user's current season
    await setDoc(userRef, {
      ...userDoc.data(),
      currentSeason: newSeason
    });

    // Create new season document with cast list
    await updateCastList(userId, newSeason, castList);
    
    return newSeason;
  } catch (error) {
    throw error;
  }
};

// Add function to get cast list for a season
export const getCastList = async (userId, season) => {
  try {
    const seasonDoc = await getDoc(doc(db, "users", userId, "seasons", season.toString()));
    return seasonDoc.exists() ? seasonDoc.data().castList : [];
  } catch (error) {
    throw error;
  }
};

// Add function to get episodes by season
export const getEpisodesBySeason = async (userId, season) => {
  try {
    const episodesRef = collection(db, "users", userId, "episodes");
    const q = query(episodesRef, where("season", "==", season));
    const episodeSnapshot = await getDocs(q);
    
    return episodeSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw error;
  }
};

// Update updateShowDetails to handle bio
export const updateShowDetails = async (userId, showName, showPicture, showBio, isPrivate) => {
  try {
    const userRef = doc(db, "users", userId);
    const updates = {};
    
    if (showName !== undefined) {
      updates.showName = showName;
    }
    
    if (showPicture !== undefined) {
      updates.showPicture = showPicture;  // Save the URL directly
    }
    
    if (showBio !== undefined) {
      updates.showBio = showBio;
    }

    if (isPrivate !== undefined) {
      updates.isPrivate = isPrivate;
    }

    await updateDoc(userRef, updates);
  } catch (error) {
    console.error("Error updating show details:", error);
    throw error;
  }
};

// Add function to update season cast
export const updateSeasonCast = async (userId, season, cast) => {
  try {
    if (cast.length > 5) {
      throw new Error('Maximum 5 cast members allowed');
    }
    
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    const updates = {
      ...userData,
      [`season${season}Cast`]: cast.filter(name => name.trim() !== '')
    };
    
    await setDoc(userRef, updates);
    return updates[`season${season}Cast`];
  } catch (error) {
    throw error;
  }
};

// Add function to get season cast
export const getSeasonCast = async (userId, season) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    return userDoc.data()[`season${season}Cast`] || [];
  } catch (error) {
    throw error;
  }
};

// Add function to update episode details
export const updateEpisodeDetails = async (userId, episodeId, updates) => {
  try {
    const episodeRef = doc(db, "users", userId, "episodes", episodeId);
    await setDoc(episodeRef, updates, { merge: true });
  } catch (error) {
    throw error;
  }
};

// Add function to get friend's show details
export const getFriendShowDetails = async (friendId) => {
  try {
    const userDoc = await getDoc(doc(db, "users", friendId));
    if (!userDoc.exists()) {
      throw new Error("User not found");
    }
    
    const userData = userDoc.data();
    return {
      showName: userData.showName || '',
      showPicture: userData.showPicture || '',
      showBio: userData.showBio || '',
      currentSeason: userData.currentSeason || 1,
      handle: userData.handle || '',
      directorName: userData.name || '',
      totalEpisodes: 0,
      season1Cast: userData.season1Cast || [],
      season2Cast: userData.season2Cast || []
    };
  } catch (error) {
    throw error;
  }
};

// Update getUserEpisodes function to sort by episode date
export const getUserEpisodes = async (userId) => {
  try {
    const episodesRef = collection(db, 'users', userId, 'episodes');
    // Sort by episode date, not createdAt
    const q = query(
      episodesRef,
      orderBy('date', 'desc')  // Sort by episode date
    );
    
    const episodesSnap = await getDocs(q);
    const episodes = episodesSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Double check the sort to ensure we're using the episode date
    episodes.sort((a, b) => {
      const dateA = a.date?.seconds ? new Date(a.date.seconds * 1000) : new Date(a.date);
      const dateB = b.date?.seconds ? new Date(b.date.seconds * 1000) : new Date(b.date);
      return dateB - dateA;  // newest first
    });

    return episodes;
  } catch (error) {
    console.error('Error getting user episodes:', error);
    return [];
  }
};

// Add a new optimized function for loading friend episodes
export const getRecentFriendEpisodes = async (friendIds) => {
  try {
    const threeDaysAgo = new Date();
    threeDaysAgo.setHours(threeDaysAgo.getHours() - 72);
    
    // Get episodes for all friends in parallel with optimized query
    const episodePromises = friendIds.map(async (friendId) => {
      const episodesRef = collection(db, 'users', friendId, 'episodes');
      const q = query(
        episodesRef,
        where('date', '>=', threeDaysAgo),
        orderBy('date', 'desc'),
        limit(10) // Limit per friend
      );
      
      const episodesSnap = await getDocs(q);
      return episodesSnap.docs.map(doc => ({
        id: doc.id,
        userId: friendId,
        ...doc.data()
      }));
    });

    const allEpisodes = await Promise.all(episodePromises);
    return allEpisodes.flat().sort((a, b) => {
      const dateA = a.date?.seconds ? a.date.seconds * 1000 : new Date(a.date).getTime();
      const dateB = b.date?.seconds ? b.date.seconds * 1000 : new Date(b.date).getTime();
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting friend episodes:', error);
    return [];
  }
};

// Add function to get user profile data
export const getUserProfile = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (!userDoc.exists()) {
      throw new Error("User not found");
    }
    
    const userData = userDoc.data();
    return {
      id: userId,
      name: userData.name,
      handle: userData.handle,
      showName: userData.showName || '',
      showPicture: userData.showPicture || '',
      showBio: userData.showBio || '',
      currentSeason: userData.currentSeason || 1,
      friendCount: userData.friendCount || 0,
      createdAt: userData.createdAt
    };
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

// Add rating function
export const rateEpisode = async (episodeOwnerId, episodeId, rating, raterId) => {
  try {
    const episodeRef = doc(db, 'users', episodeOwnerId, 'episodes', episodeId);
    const episodeDoc = await getDoc(episodeRef);
    
    if (!episodeDoc.exists()) {
      throw new Error('Episode not found');
    }

    // Get existing ratings or create new ratings map
    const existingRatings = episodeDoc.data().ratings || {};
    
    // Add or update rating
    await updateDoc(episodeRef, {
      ratings: {
        ...existingRatings,
        [raterId]: {
          score: rating,
          timestamp: serverTimestamp()
        }
      }
    });
  } catch (error) {
    console.error('Error rating episode:', error);
    throw error;
  }
};

// Update calculateStreak to handle DST properly
export const calculateStreak = (episodes) => {
  if (!episodes || episodes.length === 0) return 0;
  
  // Sort episodes by date first (newest first)
  const sortedEpisodes = [...episodes].sort((a, b) => {
    const dateA = a.date?.seconds ? new Date(a.date.seconds * 1000) : new Date(a.date);
    const dateB = b.date?.seconds ? new Date(b.date.seconds * 1000) : new Date(b.date);
    return dateB - dateA;
  });
  
  // First check if most recent episode is within 72 hours
  const mostRecentEp = sortedEpisodes[0];
  const mostRecentDate = mostRecentEp.date?.seconds ? 
    new Date(mostRecentEp.date.seconds * 1000) : 
    new Date(mostRecentEp.date);
  
  const now = new Date();
  const hoursSinceLastEp = (now - mostRecentDate) / (1000 * 60 * 60);
  
  if (hoursSinceLastEp > 72) return 0;

  // Get all unique dates
  const uniqueDates = [...new Set(sortedEpisodes.map(episode => {
    const date = episode.date?.seconds ? 
      new Date(episode.date.seconds * 1000) : 
      new Date(episode.date);
    return date.getTime();
  }))].sort((a, b) => b - a);

  if (uniqueDates.length === 0) return 0;

  let currentStreak = 1;
  let maxStreak = 1;

  // Check consecutive days with more flexible day difference range
  for (let i = 0; i < uniqueDates.length - 1; i++) {
    const diffDays = Math.abs((uniqueDates[i] - uniqueDates[i + 1]) / (1000 * 60 * 60 * 24));
    
    // Consider days consecutive if difference is between 0.85 and 1.15 days
    // This allows for DST transitions (23 or 25 hour days)
    if (diffDays >= 0.85 && diffDays <= 1.15) {
      currentStreak++;
      maxStreak = Math.max(maxStreak, currentStreak);
    } else if (diffDays < 0.1) { // Same day
      continue;
    } else {
      currentStreak = 1;
    }
  }

  return maxStreak;
};

export const calculateAverageScore = (ratings) => {
  if (!ratings || Object.keys(ratings).length === 0) return null;
  
  const now = new Date();
  const validRatings = Object.values(ratings)
    .filter(rating => {
      const ratingTime = rating.timestamp?.seconds ? 
        new Date(rating.timestamp.seconds * 1000) : 
        new Date(rating.timestamp);
      
      return (now - ratingTime) <= (72 * 60 * 60 * 1000);
    })
    .map(r => r.score);

  if (validRatings.length === 0) return null;

  const average = validRatings.reduce((a, b) => a + b, 0) / validRatings.length;
  return Number(average.toFixed(1));
};

export const handleAcceptCollab = async (userId, notificationId, episodeData) => {
  try {
    const requestRef = doc(db, 'notifications', notificationId);
    const batch = writeBatch(db);

    // Get user's current season
    const userDoc = await getDoc(doc(db, 'users', userId));
    const currentSeason = userDoc.data()?.currentSeason || 1;

    // Create a new episode document for the accepting user
    const episodeRef = doc(collection(db, 'users', userId, 'episodes'));
    
    // Create the episode data with collaboration info and current season
    const newEpisodeData = {
      title: episodeData.title,
      description: episodeData.description,
      duration: episodeData.duration,
      season: currentSeason, // Use the user's current season
      date: new Date(), // Use current date for the accepting user's episode
      imageUrl: episodeData.imageUrl,
      isCollaboration: true,
      collaborators: {
        [episodeData.collaboratorId]: {
          name: episodeData.collaboratorName,
          handle: episodeData.collaboratorHandle
        },
        [userId]: {
          name: userDoc.data().name,
          handle: userDoc.data().handle
        }
      },
      timestamp: serverTimestamp()
    };

    // Set the episode document
    batch.set(episodeRef, newEpisodeData);

    // Update notification status
    batch.update(requestRef, {
      status: 'accepted',
      acceptedAt: serverTimestamp(),
      episodeId: episodeRef.id
    });

    await batch.commit();
  } catch (error) {
    console.error('Error accepting collab:', error);
    throw error;
  }
};

export { app, analytics, auth, db, storage }; 